interface FilterOption {
  value: string;
  parentValue?: string;
  children?: string[];
}

/**
 * Toggles a filter with parent-child relationships.
 *
 * Behavior:
 * - When toggling a parent: adds/removes all children
 * - When removing a child: removes parent if it was selected
 * - When all children become selected: auto-selects parent
 *
 * @param currentFilters - Array of currently applied filter values
 * @param filterValue - The filter value being toggled
 * @param allOptions - All available filter options (to look up relationships)
 * @returns New array of filter values
 */
export function toggleHierarchicalFilter(
  currentFilters: string[],
  filterValue: string,
  allOptions: FilterOption[],
): string[] {
  const filterIsApplied = currentFilters.includes(filterValue);
  const option = allOptions.find(({ value }) => value === filterValue);
  const parentValue = option?.parentValue;
  const parentOption = allOptions.find(({ value }) => value === parentValue);

  if (filterIsApplied) {
    // Remove filter and handle parent-child relationships
    return currentFilters.filter((f) => {
      // if child filter was removed, and parent was selected, remove parent filter
      if (parentValue && f === parentValue) {
        return false;
      }
      // if parent filter was removed, remove all children
      if (option?.children?.includes(f)) {
        return false;
      }

      return f !== filterValue;
    });
  }

  // Add filter and handle parent-child relationships
  // Only add children that aren't already in the filters
  const childrenToAdd = (option?.children ?? []).filter(
    (child) => !currentFilters.includes(child),
  );

  const nextFilterState = [...currentFilters, ...childrenToAdd, filterValue];

  // If all children are now selected, also select parent
  if (
    parentValue &&
    parentOption?.children?.every((child) => nextFilterState.includes(child))
  ) {
    nextFilterState.push(parentValue);
  }

  return nextFilterState;
}
